"use client";

import * as PopoverPrimitives from "@radix-ui/react-popover";
import { useEffect, useRef, useState } from "react";
import { RiCheckLine } from "react-icons/ri";
import { cx } from "@/lib/tailwind-utils";
import { matchingOccurrence, zonedDayEndOfInstant, zonedDayStartOfInstant, zonedSetWallMinutes, zonedWallMinutes } from "@/lib/timezone-dates";
import Input from "./input";
import { ScrollShadow } from "./scroll-shadow";
import Tooltip from "./tooltip";
interface TimeInputProps {
  /** The initial Date from which to extract and manipulate the time */
  date: Date;
  /** Callback for whenever the time changes */
  onChange: (updatedDate: Date) => void;
  /** Optional className for styling */
  className?: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional accessible name when there is no associated visible label */
  ariaLabel?: string;
  /** Callback for clearing the time. When omitted, an empty input is invalid. */
  onClear?: () => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Callback for when any of the select components open/close */
  onSelectOpenChange?: (isOpen: boolean) => void;
  /** Optional start time constraint - times before this will be invalid */
  startTime?: Date;
  /** Optional error message for when time is before startTime */
  startTimeErrorMessage?: string;
  /** Time interval in minutes for the dropdown options (default: 15) */
  timeIntervalMinutes?: number;
  /** Resolve a time typed without AM/PM to whichever is closer to `date`, instead of the current clock */
  meridiemFromDate?: boolean;
  /**
   * When set, the typed/displayed wall-clock time is interpreted in this IANA
   * timezone instead of the browser's. Omit for browser-local (unchanged).
   */
  timeZone?: string;
}

/**
 * A time input component that allows both manual entry and dropdown selection.
 * Supports various input formats and shows a dropdown with configurable time intervals.
 */
/**
 * Wall-clock minutes from midnight of an instant, in `timeZone` when set, else
 * the browser's. Drives both display and highlighting.
 */
function wallMinutesIn(d: Date, timeZone: string | undefined): number {
  return timeZone ? zonedWallMinutes(d, timeZone) : d.getHours() * 60 + d.getMinutes();
}
export function TimeInput({
  date,
  onChange,
  onClear,
  className,
  placeholder = "hh:mm",
  ariaLabel,
  disabled = false,
  onSelectOpenChange,
  startTime,
  startTimeErrorMessage = "The selected time must be after start time.",
  timeIntervalMinutes = 15,
  meridiemFromDate = false,
  timeZone
}: TimeInputProps) {
  const wallMinutesOf = (d: Date): number => wallMinutesIn(d, timeZone);
  // The hour now on the same clock, for guessing an omitted AM/PM.
  const currentHourOfDay = (): number => Math.floor(wallMinutesOf(new Date()) / 60);
  const [inputValue, setInputValue] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [timeOptions, setTimeOptions] = useState<{
    display: string;
    relativeTime: string;
    fullTime: string;
    date: Date;
  }[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  // Track whether user is actively typing (vs navigating with arrow keys)
  const isUserTypingRef = useRef(false);
  // Track whether the highlight moved by arrow key, which is the only way it
  // may write back into the input. Opening the menu highlights the nearest
  // option purely to scroll to it.
  const isKeyboardNavRef = useRef(false);
  // The option arrow keys last wrote into the input. Committing it by its label
  // would collapse the second 1:30 AM of a fall-back day onto the first, so
  // blur commits its instant instead, as a click does.
  const keyboardPickRef = useRef<{
    display: string;
    date: Date;
  } | null>(null);

  // Handle open state changes
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    onSelectOpenChange?.(isOpen);
    if (isOpen) {
      isKeyboardNavRef.current = false;

      // Generate time options when opening
      const allOptions = generateTimeOptions(date);
      setTimeOptions(allOptions);

      // Find the closest option to the current date. An empty or unparseable
      // field has nothing to be near, so nothing is highlighted.
      const parsed = parseTime(inputValue);
      const target = parsed ?? (date instanceof Date && !isNaN(date.getTime()) ? date : null);
      const closestIndex = target ? findClosestTimeIndex(allOptions, target) : -1;
      setSelectedIndex(closestIndex);

      // Position the dropdown so the selected time is centered
      setTimeout(() => {
        if (closestIndex >= 0 && scrollContainerRef.current && scrollContainerRef.current.children[closestIndex] as HTMLElement) {
          const container = scrollContainerRef.current;
          const option = container.children[closestIndex] as HTMLElement;

          // Center the selected item in the dropdown
          const scrollOffset = option.offsetTop - container.clientHeight / 2 + option.clientHeight / 2;
          container.scrollTop = Math.max(0, scrollOffset);
        }
      }, 10);
    } else {
      isKeyboardNavRef.current = false;
      setSelectedIndex(-1);
    }
  };

  // Format the time for display when the component mounts or value changes
  // Use getTime() for stable dependency (avoids re-running on same-value Date reference changes)
  const dateMs = date instanceof Date && !isNaN(date.getTime()) ? date.getTime() : undefined;
  useEffect(() => {
    // Don't overwrite user's in-progress edits
    if (inputRef.current === document.activeElement) return;
    if (dateMs !== undefined) {
      const total = wallMinutesIn(new Date(dateMs), timeZone);
      const hours = Math.floor(total / 60);
      const minutes = (total % 60).toString().padStart(2, "0");
      const displayHour = hours % 12 || 12;
      const period = hours < 12 ? "AM" : "PM";
      setInputValue(`${displayHour}:${minutes} ${period}`);
    } else {
      setInputValue("");
    }
  }, [dateMs, timeZone]);

  // Format relative time from startTime
  const formatRelativeTime = (time: Date): string => {
    if (!startTime || !time) return "";
    const diffMs = time.getTime() - startTime.getTime();
    if (diffMs < 0) return "";
    const diffMins = Math.floor(diffMs / 60000);
    const hours_0 = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours_0 === 0) {
      return mins === 0 ? "" : `(${mins} min${mins !== 1 ? "s" : ""})`;
    } else if (mins === 0) {
      return `(${hours_0} hr${hours_0 !== 1 ? "s" : ""})`;
    } else {
      return `(${hours_0} hr${hours_0 !== 1 ? "s" : ""} ${mins} min${mins !== 1 ? "s" : ""})`;
    }
  };

  // Generate time options with configurable intervals
  const generateTimeOptions = (centerTime?: Date) => {
    // Ensure we have a valid date to work with
    let now: Date;
    if (centerTime && centerTime instanceof Date && !isNaN(centerTime.getTime())) {
      now = new Date(centerTime);
    } else {
      now = new Date();
    }

    // Enforce a minimum interval of 5 minutes to prevent generating too many options
    // This prevents page freezes when smallest appointment duration is very small (e.g., 1 minute)
    const MIN_INTERVAL_MINUTES = 5;
    const effectiveInterval = Math.max(timeIntervalMinutes, MIN_INTERVAL_MINUTES);

    // Generate time options with the specified interval
    const allOptions_0: {
      display: string;
      relativeTime: string;
      fullTime: string;
      date: Date;
    }[] = [];

    // If we have a startTime, use that as the base for our intervals
    // Otherwise start from midnight
    let baseTime: Date;
    if (startTime && startTime instanceof Date && !isNaN(startTime.getTime())) {
      baseTime = new Date(startTime);
    } else if (timeZone) {
      // Midnight of the *clinic-zone* day, so the enumeration spans the clinic's
      // 12AM–11:45PM rather than the browser's.
      baseTime = zonedDayStartOfInstant(now, timeZone);
    } else {
      // Create a date for midnight today
      baseTime = new Date(now);
      baseTime.setHours(0, 0, 0, 0);
    }

    // Create a date for midnight of the next day (upper bound)
    let nextMidnight: Date;
    if (timeZone) {
      nextMidnight = new Date(zonedDayEndOfInstant(now, timeZone).getTime() + 1);
    } else {
      nextMidnight = new Date(now);
      nextMidnight.setDate(nextMidnight.getDate() + 1);
      nextMidnight.setHours(0, 0, 0, 0);
    }

    // Generate options from base time to midnight next day
    const currentTime = new Date(baseTime);
    // Zoned options step in absolute minutes: browser-local setters would
    // skip or repeat an hour on the browser's own DST change, which needn't
    // fall on the clinic's.
    const advance = (time_0: Date) => {
      if (timeZone) {
        time_0.setTime(time_0.getTime() + effectiveInterval * 60_000);
      } else {
        time_0.setMinutes(time_0.getMinutes() + effectiveInterval);
      }
    };
    while (currentTime < nextMidnight) {
      // Skip the first option if it matches the startTime exactly
      if (startTime && currentTime.getTime() === startTime.getTime()) {
        // Increment by the effective interval
        advance(currentTime);
        continue;
      }
      if (!isNaN(currentTime.getTime())) {
        // Options are absolute instants, so label them by their clinic
        // wall-clock whenever a zone is set — for the start-time picker too,
        // otherwise the dropdown would offer (and sync back) browser-local
        // labels that get re-parsed as clinic times on blur.
        const labelMinutes = wallMinutesOf(currentTime);
        const hours_1 = Math.floor(labelMinutes / 60);
        const minutes_0 = (labelMinutes % 60).toString().padStart(2, "0");
        const displayHour_0 = hours_1 % 12 || 12;
        const period_0 = hours_1 < 12 ? "AM" : "PM";
        const relativeTime = startTime ? formatRelativeTime(currentTime) : "";
        const timeDisplay = `${displayHour_0}:${minutes_0} ${period_0}`;
        allOptions_0.push({
          display: timeDisplay,
          relativeTime: relativeTime,
          fullTime: relativeTime ? `${timeDisplay} ${relativeTime}` : timeDisplay,
          date: new Date(currentTime)
        });
      }

      // Increment by the effective interval
      advance(currentTime);
    }
    return allOptions_0;
  };

  // New function to find the index of the closest time option
  const findClosestTimeIndex = (options: {
    date: Date;
  }[], targetDate: Date): number => {
    if (!options.length) return 0;
    let closestIndex_0 = 0;
    let closestDiff = Infinity;
    options.forEach((option_0, index) => {
      const diff = Math.abs(option_0.date.getTime() - targetDate.getTime());
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex_0 = index;
      }
    });
    return closestIndex_0;
  };

  // Check if a date is valid according to startTime constraint
  const isValidDate = (date_0: Date): boolean => {
    if (!startTime || !date_0) return true;
    return date_0 >= startTime;
  };

  // Picks AM/PM for a time typed without either
  const guessIsPM = (hours_2: number, minutes_1: number): boolean => {
    if (meridiemFromDate && date instanceof Date && !isNaN(date.getTime())) {
      const target_0 = wallMinutesOf(date);
      const am = hours_2 % 12 * 60 + minutes_1;
      return Math.abs(am + 12 * 60 - target_0) < Math.abs(am - target_0);
    }
    return hours_2 <= 12 && currentHourOfDay() >= 12;
  };

  // Parse the input time and update the date
  const parseTime = (input: string): Date | null => {
    try {
      // Remove relative time information if present
      const cleanedInput = input.replace(/\s*\([^)]*\)\s*$/, "");

      // Check for AM/PM indicators with more flexible pattern
      const pmMatch = /[pP](?:[mM])?/.test(cleanedInput);
      const amMatch = /[aA](?:[mM])?/.test(cleanedInput);
      let isPM = pmMatch;

      // Remove all non-alphanumeric characters for numeric parsing
      const cleanInput = cleanedInput.replace(/[^0-9a-zA-Z]/g, "");

      // Remove AM/PM indicators for numeric parsing
      const numericPart = cleanInput.replace(/[aApP][mM]?/g, "");
      let hours_3 = 0;
      let minutes_2 = 0;
      if (amMatch || pmMatch) {
        // Process with AM/PM indicator
        if (numericPart.length <= 2) {
          // Just hours
          hours_3 = parseInt(numericPart, 10);
          minutes_2 = 0;
        } else if (numericPart.length <= 4) {
          // Hours and minutes
          if (numericPart.length === 3) {
            hours_3 = parseInt(numericPart[0], 10);
            minutes_2 = parseInt(numericPart.substring(1), 10);
          } else {
            hours_3 = parseInt(numericPart.substring(0, 2), 10);
            minutes_2 = parseInt(numericPart.substring(2), 10);
          }
        } else {
          return null; // Invalid format
        }
      } else {
        // No AM/PM indicator, try to parse as numeric
        if (cleanInput.length <= 2) {
          // Just hours
          hours_3 = parseInt(cleanInput, 10);
          minutes_2 = 0;
          isPM = guessIsPM(hours_3, minutes_2);
        } else if (cleanInput.length <= 4) {
          // Hours and minutes
          if (cleanInput.length === 3) {
            hours_3 = parseInt(cleanInput[0], 10);
            minutes_2 = parseInt(cleanInput.substring(1), 10);
          } else {
            hours_3 = parseInt(cleanInput.substring(0, 2), 10);
            minutes_2 = parseInt(cleanInput.substring(2), 10);
          }
          isPM = guessIsPM(hours_3, minutes_2);
        } else {
          return null; // Invalid format
        }
      }

      // Validate hours and minutes
      if (isNaN(hours_3) || isNaN(minutes_2) || hours_3 < 1 || hours_3 > 12 || minutes_2 < 0 || minutes_2 > 59) {
        return null;
      }

      // Convert to 24-hour format
      let adjustedHours = hours_3 % 12;
      if (isPM) {
        adjustedHours += 12;
      }

      // Create a new date with the parsed time
      // Make sure we have a valid date to start with
      const baseDate = date instanceof Date && !isNaN(date.getTime()) ? new Date(date) : new Date();

      // In zoned mode the picked wall-clock time is set on the base date's
      // clinic-zone calendar day; otherwise it's browser-local as before.
      // A time inside the hour that repeats when clocks fall back keeps the
      // base date's side of the change, as the dropdown does.
      const newDate = timeZone ? matchingOccurrence(zonedSetWallMinutes(baseDate, adjustedHours * 60 + minutes_2, timeZone), baseDate, timeZone) : (() => {
        const d_0 = new Date(baseDate);
        d_0.setHours(adjustedHours, minutes_2, 0, 0);
        return d_0;
      })();

      // Final validation
      if (isNaN(newDate.getTime())) {
        return null;
      }

      // Check if the time is valid according to startTime constraint
      if (!isValidDate(newDate)) {
        return null;
      }
      return newDate;
    } catch {
      return null;
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    isUserTypingRef.current = true;
    keyboardPickRef.current = null;
    setInputValue(newValue);

    // Clear error when user starts typing again
    if (error) {
      setError(null);
      setShowTooltip(false);
    }

    // Open dropdown if not already open
    if (!open) {
      setOpen(true);
      onSelectOpenChange?.(true);
    }
  };
  const handleBlur = (e_0: React.FocusEvent<HTMLInputElement>) => {
    isUserTypingRef.current = false;

    // Don't process if clicking inside dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e_0.relatedTarget as Node)) {
      return;
    }
    if (open) handleOpenChange(false);

    // Process the input value
    try {
      if (onClear && inputValue.trim() === "") {
        onClear();
        setError(null);
        setShowTooltip(false);
        return;
      }
      const keyboardPick = keyboardPickRef.current;
      keyboardPickRef.current = null;
      const parsedDate = timeZone && keyboardPick?.display === inputValue ? keyboardPick.date : parseTime(inputValue);
      if (!parsedDate) {
        // Check if the error is due to startTime constraint
        const basicParsedDate = parseTime(inputValue.replace(/\s*\([^)]*\)\s*$/, ""));
        if (basicParsedDate && startTime && !isValidDate(basicParsedDate)) {
          setError(startTimeErrorMessage);
          setInputValue(""); // Clear the input on invalid time
        } else {
          setError("Invalid time format");
          setInputValue(""); // Clear the input on invalid time
        }
        setShowTooltip(true);
        return;
      }

      // Validate against startTime constraint
      if (!isValidDate(parsedDate)) {
        setError(startTimeErrorMessage);
        setInputValue(""); // Clear the input on invalid time
        setShowTooltip(true);
        return;
      }

      // Update with the valid date
      onChange(parsedDate);

      // Format the input for display
      const totalMin = wallMinutesOf(parsedDate);
      const hours_4 = Math.floor(totalMin / 60);
      const minutes_3 = (totalMin % 60).toString().padStart(2, "0");
      const displayHour_1 = hours_4 % 12 || 12;
      const period_1 = hours_4 < 12 ? "AM" : "PM";
      const relativeTime_0 = startTime ? formatRelativeTime(parsedDate) : "";
      setInputValue(relativeTime_0 ? `${displayHour_1}:${minutes_3} ${period_1} ${relativeTime_0}` : `${displayHour_1}:${minutes_3} ${period_1}`);
      setError(null);
      setShowTooltip(false);
    } catch {
      setError("Invalid time format");
      setInputValue(""); // Clear the input on invalid time
      setShowTooltip(true);
    }
  };
  const handleKeyDown = (e_1: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_1.key === "Enter") {
      e_1.preventDefault();
      inputRef.current?.blur();
    } else if (e_1.key === "ArrowDown") {
      e_1.preventDefault();
      isUserTypingRef.current = false;
      isKeyboardNavRef.current = true;
      if (!open) {
        setOpen(true);
        onSelectOpenChange?.(true);
      } else {
        setSelectedIndex(prev => Math.min(prev + 1, timeOptions.length - 1));
      }
    } else if (e_1.key === "ArrowUp") {
      e_1.preventDefault();
      isUserTypingRef.current = false;
      isKeyboardNavRef.current = true;
      if (!open) {
        setOpen(true);
        onSelectOpenChange?.(true);
      } else {
        setSelectedIndex(prev_0 => Math.max(prev_0 - 1, 0));
      }
    } else if (e_1.key === "Escape") {
      e_1.preventDefault();
      setOpen(false);
      onSelectOpenChange?.(false);
    } else if (e_1.key === "Tab") {
      setOpen(false);
      onSelectOpenChange?.(false);
    }
  };
  const selectTime = (option_1: (typeof timeOptions)[0]) => {
    setInputValue(option_1.display); // Use display instead of fullTime
    // A zoned option is already the instant it names; reparsing its label
    // would collapse the second 1:30 AM of a fall-back day onto the first.
    const parsedDate_0 = timeZone ? option_1.date : parseTime(option_1.display);
    if (parsedDate_0) {
      // Validate against startTime constraint
      if (!isValidDate(parsedDate_0)) {
        setError(startTimeErrorMessage);
        setInputValue(""); // Clear the input on invalid time
        setShowTooltip(true);
        return;
      }
      onChange(parsedDate_0);
      setError(null);
      setShowTooltip(false);
    } else {
      setError("Invalid time format");
      setInputValue(""); // Clear the input on invalid time
      setShowTooltip(true);
    }
    setOpen(false);
    onSelectOpenChange?.(false);
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && scrollContainerRef.current) {
      const selectedElement = scrollContainerRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest"
        });
      }
    }
  }, [selectedIndex]);

  // Handle selecting with keyboard (only when using arrow keys, not when typing)
  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < timeOptions.length && open && isKeyboardNavRef.current && !isUserTypingRef.current) {
      // When navigating with keyboard, only show the clean time display, not the full time with relative info
      const option_2 = timeOptions[selectedIndex];
      keyboardPickRef.current = option_2;
      setInputValue(option_2.display); // Use display instead of fullTime
    }
  }, [selectedIndex, timeOptions, open]);
  return <PopoverPrimitives.Root open={open} onOpenChange={handleOpenChange}>
      <div className="relative" ref={tooltipRef}>
        <Tooltip content={error || "Invalid time format"} open={showTooltip && !!error} onOpenChange={setShowTooltip} side="top">
          <PopoverPrimitives.Trigger asChild>
            <Input ref={inputRef} type="text" value={inputValue} onChange={handleChange} onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder={placeholder} disabled={disabled}
          // Geometry, colors and focus come from Input itself, so this
          // field keeps the same radius and height as the date picker
          // standing next to it.
          hasError={!!error} className={className} aria-label={ariaLabel} aria-invalid={!!error} />
          </PopoverPrimitives.Trigger>
        </Tooltip>

        <PopoverPrimitives.Portal>
          <PopoverPrimitives.Content ref={dropdownRef} sideOffset={4} side="bottom" align="start" avoidCollisions onOpenAutoFocus={e_2 => e_2.preventDefault()} className={cx("bg-background/70 backdrop-blur-sm", " rounded-md text-body shadow-lg ring-1 ring-border/5 focus:outline-none", "z-50", startTime ? "min-w-54 w-auto whitespace-nowrap" : "w-[var(--radix-popover-trigger-width)]",
        // transition
        "will-change-[transform,opacity]", "data-[state=closed]:animate-hide", "data-[state=open]:data-[side=bottom]:animate-slideDownAndFade data-[state=open]:data-[side=left]:animate-slideLeftAndFade data-[state=open]:data-[side=right]:animate-slideRightAndFade data-[state=open]:data-[side=top]:animate-slideUpAndFade")}>
            <ScrollShadow.Root>
              <ScrollShadow.Viewport ref={scrollContainerRef} className={cx(
            // base
            "max-h-60 overflow-auto py-1")}>
                {timeOptions.map((option_3, index_0) => {
                // Match the option to the selected value by comparing both in
                // the same (effective) zone — wallMinutesOf is zoned when a
                // timeZone is set, browser-local otherwise — so highlighting
                // stays correct when the clinic and browser zones differ. The
                // hour bound tells apart the two 1:30 AMs of a fall-back day.
                const isCurrentlySelected = date && wallMinutesOf(option_3.date) === wallMinutesOf(date) && Math.abs(option_3.date.getTime() - date.getTime()) < 3_600_000;
                return <div key={`${option_3.display}-${index_0}`} className={cx("grid cursor-pointer grid-cols-[1fr_20px] gap-x-2 px-2.5 py-1.5 outline-none transition-colors",
                // focus/hover states like select component
                "hover:bg-hover focus-visible:bg-hover",
                // selected state - just bold text like select
                selectedIndex === index_0 && "font-semibold",
                // currently selected time gets bold text
                isCurrentlySelected && "font-semibold")} onClick={() => selectTime(option_3)} tabIndex={0} role="option" aria-selected={selectedIndex === index_0}>
                      <div className="flex-1 truncate">
                        {option_3.display}
                        {option_3.relativeTime && <span className="ml-1 text-foreground-secondary">
                            {option_3.relativeTime}
                          </span>}
                      </div>
                      {isCurrentlySelected && <RiCheckLine className="size-5 shrink-0 text-foreground" aria-hidden="true" />}
                    </div>;
              })}
              </ScrollShadow.Viewport>
              <ScrollShadow.Top />
              <ScrollShadow.Bottom />
            </ScrollShadow.Root>
          </PopoverPrimitives.Content>
        </PopoverPrimitives.Portal>
      </div>
    </PopoverPrimitives.Root>;
}

/**
 * Example usage:
 *
 * ```tsx
 * import { useState } from 'react';
 * import { TimeInput } from '@/components/ui';
 *
 * function MyComponent() {
 *   const [date, setDate] = useState<Date>(new Date());
 *
 *   return (
 *     <div>
 *       <TimeInput
 *         date={date}
 *         onChange={(newDate) => setDate(newDate)}
 *         placeholder="Enter time (e.g. 1:30 PM or 1330)"
 *       />
 *     </div>
 *   );
 * }
 * ```
 *
 * The component accepts the following input formats:
 * - "1:30 PM", "1:30PM", "1:30 pm", "1:30pm"
 * - "1:30", "130", "1:30" (will default to AM/PM based on current time)
 * - "1" (will default to the hour with 00 minutes)
 * - "1p", "1pm", "1 pm", "1 p" (shorthand for PM)
 * - "1a", "1am", "1 am", "1 a" (shorthand for AM)
 */